import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useColumnVisibilityStorage } from './useColumnVisibilityStorage'

type SchemaKey<TSchema extends TableSchema> = Extract<keyof TSchema, string>

const getColumnName = (
  key: string,
  title?: TableSchemaItem['title'],
  selectorLabel?: string
) => {
  if (selectorLabel) return selectorLabel
  if (typeof title === 'string' || typeof title === 'number') {
    return String(title)
  }

  return key
}

const mapColumnsByLabel = (columns: ColumnSelectorColumn[]) =>
  new Map(columns.map(column => [column.label, column]))

const buildColumns = <TSchema extends TableSchema>(
  schema: TSchema,
  readonlyKeys: ReadonlyArray<SchemaKey<TSchema>>,
  currentColumns: ColumnSelectorColumn[] = [],
  storedSelectionByLabel: Record<string, boolean> | null = null
): ColumnSelectorColumn[] => {
  const readonlyKeysSet = new Set<SchemaKey<TSchema>>(readonlyKeys)
  const currentColumnsMap = mapColumnsByLabel(currentColumns)
  const hasStoredSelection = storedSelectionByLabel !== null
  const schemaKeys = Object.keys(schema) as SchemaKey<TSchema>[]

  return schemaKeys.map(key => {
    const currentColumn = currentColumnsMap.get(key)
    const isReadonly = readonlyKeysSet.has(key)
    const value = schema[key]
    // Colunas marcadas como `optional` no schema nascem ocultas (disponíveis
    // via seletor); as demais nascem visíveis.
    const defaultSelection = hasStoredSelection
      ? (storedSelectionByLabel?.[key] ?? !value.optional)
      : !value.optional
    const isSelected = isReadonly
      ? true
      : (currentColumn?.isSelected ?? defaultSelection)

    return {
      name: getColumnName(key, value.title, value.selectorLabel),
      label: key,
      isReadonly,
      isSelected
    }
  })
}

export const useColumnVisibility = <TSchema extends TableSchema = TableSchema>({
  schema,
  tableName,
  readonlyKeys = []
}: UseColumnVisibilityOptions<TSchema>): UseColumnVisibilityResult<TSchema> => {
  const readonlyKeysSignature = readonlyKeys.join('|')
  const stableReadonlyKeys = useMemo<SchemaKey<TSchema>[]>(
    () =>
      readonlyKeysSignature
        ? (readonlyKeysSignature.split('|') as SchemaKey<TSchema>[])
        : [],
    [readonlyKeysSignature]
  )
  const { storedSelectionByLabel, persistSelectedLabels } =
    useColumnVisibilityStorage({
      tableName
    })
  const hasMountedRef = useRef(false)

  const [columns, setColumns] = useState<ColumnSelectorColumn[]>(() =>
    buildColumns(schema, stableReadonlyKeys, [], storedSelectionByLabel)
  )

  useEffect(() => {
    setColumns(currentColumns =>
      buildColumns(
        schema,
        stableReadonlyKeys,
        currentColumns,
        storedSelectionByLabel
      )
    )
  }, [schema, stableReadonlyKeys, storedSelectionByLabel])

  const onColumnsChange = useCallback(
    (nextColumns: ColumnSelectorColumn[]) => {
      setColumns(
        buildColumns(
          schema,
          stableReadonlyKeys,
          nextColumns,
          storedSelectionByLabel
        )
      )
    },
    [schema, stableReadonlyKeys, storedSelectionByLabel]
  )

  const onResetToDefault = useCallback(() => {
    setColumns(buildColumns(schema, stableReadonlyKeys, [], null))
  }, [schema, stableReadonlyKeys])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }

    persistSelectedLabels(columns)
  }, [columns, persistSelectedLabels])

  const visibleColumnsSchema = useMemo(() => {
    const selectedKeys = new Set(
      columns.filter(column => column.isSelected).map(column => column.label)
    )
    const schemaKeys = Object.keys(schema) as SchemaKey<TSchema>[]

    return schemaKeys.reduce((acc, key) => {
      if (selectedKeys.has(key)) {
        acc[key] = schema[key]
      }

      return acc
    }, {} as TSchema)
  }, [columns, schema])

  return {
    columns,
    visibleColumnsSchema,
    onColumnsChange,
    onResetToDefault
  }
}

export default useColumnVisibility
